import { Link, useRouterState } from '@tanstack/react-router'
import { Users } from 'lucide-react'
import type { AccommodationDetail } from '@/api/accommodation/types'
import { Typography } from '@/components/ui/typography'
import { cn, getPersonsLabel } from '@/lib/utils'

interface DetailUnitSwitcherProps {
  data: AccommodationDetail
}

/**
 * The units of a place with several of them (rooms of a guesthouse), the one
 * on the page highlighted. Renders nothing for a place with a single unit, so
 * a cottage keeps its page as it was.
 */
export function DetailUnitSwitcher({ data }: DetailUnitSwitcherProps) {
  // Carried over so "Zpět na vyhledávání" still returns to the filtered search.
  const backHref = useRouterState({ select: (s) => s.location.state.backHref })

  if (data.unit.siblings.length === 0) return null

  // The owner's order, the same on every unit's page.
  const units = [data.unit, ...data.unit.siblings].sort((a, b) => a.position - b.position)

  return (
    <div className="mt-8">
      <Typography variant="h2" className="mb-4">
        Vyberte si ubytování
      </Typography>
      <ul className="-mx-4 flex snap-x scroll-px-4 gap-3 overflow-x-auto px-4 pb-1 sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-0 sm:pb-0">
        {units.map((unit) => {
          const isCurrent = unit.id === data.unit.id
          return (
            <li key={unit.id} className="w-56 shrink-0 snap-start sm:w-auto">
              <Link
                to="/ubytovani/$slug/$unitSlug"
                params={{ slug: data.slug, unitSlug: unit.slug }}
                state={{ backHref }}
                className={cn(
                  'flex h-full flex-col gap-2 rounded-xl border p-4 transition-colors',
                  isCurrent ? 'border-red bg-red-50/50' : 'border-slate-200 hover:border-slate-400',
                )}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-medium text-slate-900">{unit.name}</span>
                  {isCurrent && <span className="text-red shrink-0 text-xs font-medium">Prohlížíte</span>}
                </div>
                <span className="inline-flex items-center gap-1.5 text-sm text-slate-600">
                  <Users className="h-4 w-4 text-slate-500" />
                  {unit.type.name} · {unit.totals.beds}&nbsp;
                  {getPersonsLabel(unit.totals.beds)}
                </span>
              </Link>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
